import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup } from 'reactstrap';
import Button from '../../../../components/formControls/Button';
import Select2 from '../../../../components/formControls/Select2';
import {
  isSaveUserError,
  isSaveUserLoaded,
} from '../../../../js/virtualOperator/selectors';
import { saveVirtualOperatorUserRequest } from '../../../../js/virtualOperator/actions';
import ToastMessage from '../../../../components/messages/ToastMessage';
import Label from '../../../../components/formControls/Label';
import Input from '../../../../components/formControls/Input';
import { VirtualOperatorEnums } from '../../../../js/virtualOperator/VirtualOperatorUtils';
import { getPbxEditingQueues } from '../../../../js/phone/selectors';
import TextArea from '../../../../components/formControls/Textarea';

const messages = defineMessages({
  title: {
    id: 'PbxVirtualOperatorUserModalEdit.title',
    defaultMessage: 'Edit {fullname}',
  },
  alias: {
    id: 'PbxVirtualOperatorUserModalEdit.label.alias',
    defaultMessage: 'Alias',
  },
  aliasMessage: {
    id: 'PbxVirtualOperatorUserModalEdit.label.aliasMessage',
    defaultMessage: 'If there is a value, it is the name that the user is known by in the virtual operator',
  },
  enabled: {
    id: 'PbxVirtualOperatorUserModalEdit.label.enabled',
    defaultMessage: 'Enabled',
  },
  enabledYes: {
    id: 'PbxVirtualOperatorUserModalEdit.label.enabledYes',
    defaultMessage: 'Yes',
  },
  enabledNo: {
    id: 'PbxVirtualOperatorUserModalEdit.label.enabledNo',
    defaultMessage: 'No',
  },
  office: {
    id: 'PbxVirtualOperatorUserModalEdit.office',
    defaultMessage: 'Office',
  },
  pronunciation: {
    id: 'PbxVirtualOperatorUserModalEdit.pronunciation',
    defaultMessage: 'Additional pronunciation',
  },
  transfer: {
    id: 'PbxVirtualOperatorUserModalEdit.transfer',
    defaultMessage: 'Transfer profile',
  },
  BLIND: {
    id: 'PbxVirtualOperatorUserModalEdit.BLIND',
    defaultMessage: 'Blind',
  },
  SUPERVISED: {
    id: 'PbxVirtualOperatorUserModalEdit.SUPERVISED',
    defaultMessage: 'Supervised',
  },
  CONSULTATION: {
    id: 'PbxVirtualOperatorUserModalEdit.CONSULTATION',
    defaultMessage: 'Consultation',
  },
  save: {
    id: 'PbxVirtualOperatorUserModalEdit.button.save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'PbxVirtualOperatorUserModalEdit.button.cancel',
    defaultMessage: 'Cancel',
  },
  errorSaving: {
    id: 'PbxVirtualOperatorUserModalEdit.errorSaving',
    defaultMessage: 'Error saving',
  },
  missingTransfer: {
    id: 'PbxVirtualOperatorUserModalEdit.missingTransfer',
    defaultMessage: 'Transfer profile is required when enabled',
  },
});

class PbxVirtualOperatorUserModalEdit extends Component {
  constructor(props) {
    super(props);
    const selectedOffice = props.user.office && props.offices.find(
      (office) => office.queueId === props.user.office
    );
    this.state = {
      errors: {},
      enabled: props.user.enabled,
      alias: props.user.alias,
      pronunciation: props.user.pronunciation,
      transfer: props.user.transfer
        ? {
            value: props.user.transfer,
            label: props.intl.formatMessage(messages[props.user.transfer]),
          }
        : { value: null, label: '' },
      office: props.user.office
        ? {
            value: props.user.office,
            label: selectedOffice
              ? `${selectedOffice.id}${
                selectedOffice.name && selectedOffice.name !== selectedOffice.id ? ` (${selectedOffice.name})` : ''
              }${selectedOffice.description ? ` - ${selectedOffice.description}` : ''}`
              : props.user.office,
          }
        : { value: null, label: '' },
    };
  }

  componentDidUpdate = (prevProps) => {
    if (
      this.props.saveLoaded &&
      !prevProps.saveLoaded &&
      !this.props.saveError
    ) {
      this.props.onClose();
    }
  };

  handleEnabled = (e) => {
    this.setState({
      enabled: e.target.value === 'Y',
      errors: {},
    });
  };

  handleAlias = (e) => {
    this.setState({
      alias: e.target.value,
    });
  };

  handlePronunciation = (e) => {
    this.setState({
      pronunciation: e.target.value,
    });
  };

  handleTransfer = (selected) => {
    this.setState({
      transfer: selected,
      errors: {},
    });
  };

  handleOffice = (selected) => {
    this.setState({
      office: selected,
    });
  };

  save = () => {
    if (this.state.enabled && !this.state.transfer?.value) {
      this.setState({
        errors: {
          ...this.state.errors,
          transfer: this.props.intl.formatMessage(messages.missingTransfer),
        },
      });
      return;
    }
    this.props.saveUser({
      id: this.props.user.id,
      data: {
        alias: this.state.alias && this.state.alias.trim() ? this.state.alias.trim() : null,
        enabled: this.state.enabled,
        pronunciation: this.state.pronunciation,
        transfer: this.state.transfer ? this.state.transfer.value : null,
        office: this.state.office ? this.state.office.value : null,
      },
    });
  };

  render() {
    const {
      isOpen,
      onClose,
      saveLoaded,
      saveError,
      offices,
      intl: { formatMessage },
    } = this.props;
    const transferOptions = [
      {
        value: null,
        label: '',
      },
    ];
    Object.keys(VirtualOperatorEnums.TransferProfile).forEach((key) => {
      transferOptions.push({
        value: key,
        label: formatMessage(messages[key]),
      });
    });
    const officeOptions = offices.map((office) => ({
      value: office.id,
      label: `${office.id}${
        office.name && office.name !== office.id ? ` (${office.name})` : ''
      }${office.description ? ` - ${office.description}` : ''}`,
    }));
    officeOptions.unshift({
      value: null,
      label: '',
    });

    return (
      <Modal isOpen={isOpen}>
        <ModalHeader toggle={onClose}>
          {formatMessage(messages.title, {
            fullname: this.props.user.fullname,
          })}
        </ModalHeader>

        <ModalBody>
          {saveError && (
            <ToastMessage
              type="danger"
              text={formatMessage(messages.errorSaving)}
              closeTimeout={5000}
            />
          )}
          <div className="row">
            <FormGroup>
              <Label for="enabled" className="px-2">
                {formatMessage(messages.enabled)}
              </Label>
              <div className="px-3">
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="enabled_yes"
                    onChange={this.handleEnabled}
                    checked={this.state.enabled}
                    name="enabled"
                    value="Y"
                    className="custom-control-input"
                  />
                  <label className="custom-control-label" htmlFor="enabled_yes">
                    {formatMessage(messages.enabledYes)}
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="enabled_no"
                    onChange={this.handleEnabled}
                    checked={!this.state.enabled}
                    name="enabled"
                    value="N"
                    className="custom-control-input"
                  />
                  <label className="custom-control-label" htmlFor="enabled_no">
                    {formatMessage(messages.enabledNo)}
                  </label>
                </div>
              </div>
            </FormGroup>
          </div>
          <div className="row">
            <div className="col-xl-12 p-3">
              <Label for="alias">
                {formatMessage(messages.alias)}
              </Label>
              <Input
                id="alias"
                name="alias"
                type="text"
                onChange={this.handleAlias}
                value={this.state.alias}
              />
              <small className="form-text text-muted">
                {formatMessage(messages.aliasMessage)}
              </small>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-12 p-3">
              <Label for="office">
                {formatMessage(messages.office)}
              </Label>
              <Select2
                id="office"
                name="office"
                options={officeOptions}
                isClearable
                onChange={this.handleOffice}
                value={this.state.office}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xl-12 p-3">
              <Label for="pronunciation">
                {formatMessage(messages.pronunciation)}
              </Label>
              <TextArea
                maxLength={5000}
                id="pronunciation"
                name="pronunciation"
                onChange={this.handlePronunciation}
                value={this.state.pronunciation}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xl-12 p-3">
              <Label for="transfer">{formatMessage(messages.transfer)}</Label>
              <Select2
                id="transfer"
                name="transfer"
                options={transferOptions}
                isClearable
                onChange={this.handleTransfer}
                value={this.state.transfer}
                error={this.state.errors.transfer}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={this.save}
            text={formatMessage(messages.save)}
            loading={!saveLoaded}
          />
          <button
            type="button"
            className="btn btn-outline-primary ml-2"
            onClick={onClose}
          >
            {formatMessage(messages.cancel)}
          </button>
        </ModalFooter>
      </Modal>
    );
  }
}

PbxVirtualOperatorUserModalEdit.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    saveLoaded: isSaveUserLoaded(state),
    saveError: isSaveUserError(state),
    offices: getPbxEditingQueues(state),
  };
}

export default injectIntl(
  connect(mapStateToProps, {
    saveUser: saveVirtualOperatorUserRequest,
  })(PbxVirtualOperatorUserModalEdit)
);
